import { createSelector } from "reselect";

import { RootState } from "redux/reducers/rootReducer";
import { parseFullUserName } from "utils/parseFullUserName";

const getUserState = (state: RootState) => state.user;

export const getFullUser = createSelector([getUserState], (user) => user);
export const getUserId = createSelector([getUserState], (user) => user.id);
export const getUserFavoriteStrains = createSelector(
  [getUserState],
  (user) => user.favoritedStrains
);
export const getUserFollowDispensaries = createSelector(
  [getUserState],
  (user) => user.followedDispensaries
);
export const getUserSubmittedReviews = createSelector(
  [getUserState],
  (user) => user.reviews
);
export const getIsUserLoading = createSelector(
  [getUserState],
  (user) => user.loading
);
export const getIsLoggedIn = createSelector(
  [getUserState],
  (user) => !!(user?.id && user?.id > 0)
);
export const getIsLoggedOutRedirect = createSelector(
  [getUserState],
  (user) => user?.isLoggedOutRedirect
);

const getClaims = createSelector([getUserState], (user) => user.claims);
const hasClaim = (
  claims: Record<string, string> | undefined | null,
  claim: string
) => {
  return !!(claims && claims["http://schemas.leafly.com/claims/" + claim]);
};

export const getUserIsBrandAdmin = createSelector(
  [getClaims],
  (claims) =>
    hasClaim(claims, "brand-owner") || hasClaim(claims, "products-and-brands")
);
export const getUserIsDispensaryAdmin = createSelector([getClaims], (claims) =>
  hasClaim(claims, "store-admin")
);
export const getUserIsEnterpriseAccountAdmin = createSelector(
  [getClaims],
  (claims) => hasClaim(claims, "organization-admin")
);
export const getUserIsWordpressAdmin = createSelector([getClaims], (claims) =>
  hasClaim(claims, "cms-role")
);

export const getUserPrefersMedical = createSelector(
  [getUserState],
  (user) => user.prefersMedical
);

export const getUserFulfillmentPreference = createSelector(
  [getUserState],
  (user) => user.fulfillmentPreference
);

export const getUserMedCard = createSelector(
  [getUserState],
  (user) => user.medCard
);

const getUserFullName = createSelector(
  [getUserState],
  (user) => (user && user.name) || ""
);

export const getUserNameParsed = createSelector(
  [getUserFullName],
  parseFullUserName
);

export const getUserEmail = createSelector(
  [getUserState],
  (user) => (user && user.email) || ""
);

export const getUserPhone = createSelector(
  [getUserState],
  (user) => (user && user.phoneNumber) || ""
);

export const getUserDateOfBirth = createSelector(
  [getUserState],
  (user) => (user && user.dateOfBirth) || ""
);
export const userGovernmentIdImageInfoSelector = createSelector(
  [getUserState],
  (user) => user.governmentCardImageData
);

export const userMedicalCardImageInfoSelector = createSelector(
  [getUserState],
  (user) => user.medicalCardImageData
);

export const getCardImageDataFromType = (type: "government" | "medical") => {
  return type === "government"
    ? userGovernmentIdImageInfoSelector
    : userMedicalCardImageInfoSelector;
};

export const getCardImageLoadingFromType = (type: "government" | "medical") =>
  createSelector([getCardImageDataFromType(type)], (data) => data?.loading);
